import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import SummaryCards from "components/SummaryCards";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import CustomPagination from "components/CustomPagination";
import * as api from "constants/api_constants";
import { get, post, uri } from "helpers/api_helper";
import { showErrorToast } from "helpers/toastMessages";

const Candidates = () => {
  const navigate = useNavigate();
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState({
    totalCount: 0,
    completed: 0,
    declinedOffer: 0,
    hired: 0,
    inProgress: 0,
    putOnHold: 0,
    rejected: 0,
  });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [fileModal, setFileModal] = useState({ open: false, url: "" });
  const timerRef = useRef(null);

  // Fetch candidates with pagination, search, and status filter
  const fetchCandidatesDetails = useCallback(
    async (page = 1, query = "", status = "") => {
      try {
        setApiStatus({ loading: true, error: "" });
        const response = await post(api.GET_ALL_CANDIDATES, {
          page: page - 1, // API expects 0-based page index
          pageSize: 10,
          orderBy: "id",
          sort: "asc",
          searchKeyword: query,
          status: status || null,
        });
        console.log("Response:", response);

        const candidateData = response?.data?.data || [];
        // Deduplicate candidates by id and select relevant fields
        const uniqueCandidates = Array.from(
          new Map(
            candidateData.map((candidate) => [
              candidate.id,
              {
                candidateId: candidate.candidateId,
                generalInfoCandidateDto: candidate.generalInfoCandidateDto,
                submittedOn: candidate.appliedDate || "N/A",
                id: candidate.id,
                jobId: candidate.job?.[0]?.jobId || "N/A",
                jobRole: candidate.job?.[0]?.jobTitle || "N/A",
                resumeId: candidate.resumeSupportingDocDto?.uploadResume || null,
              },
            ])
          ).values()
        );

        console.log("uniqueCandidates:", uniqueCandidates);
        setCandidateDetails(uniqueCandidates);
        setTotalPages(response?.data?.totalPages || 0);
        setApiStatus({ loading: false, error: "" });
      } catch (error) {
        console.error("Error fetching candidates:", error);
        setApiStatus({
          loading: false,
          error: "Candidates data fetching failed",
        });
        showErrorToast("Candidates data fetching failed");
      }
    },
    []
  );

  // Fetch candidate counts
  const fetchCount = useCallback(async () => {
    try {
      setApiStatus((prev) => ({ ...prev, loading: true }));
      const response = await get(api.CANDIDATE_TOTAL_COUNT);
      console.log("Candidate count response:", response);
      setTotalCount(
        response || {
          totalCount: 0,
          completed: 0,
          declinedOffer: 0,
          hired: 0,
          inProgress: 0,
          putOnHold: 0,
          rejected: 0,
        }
      );
      setApiStatus((prev) => ({ ...prev, loading: false }));
    } catch (error) {
      console.error("Error fetching count:", error);
      setApiStatus({
        loading: false,
        error: "Failed to get count",
      });
      showErrorToast("Failed to get count");
    }
  }, []);

  // Handle resume viewing
  const handleViewResume = async (resumeId) => {
    if (!resumeId) {
      showErrorToast("No resume available for this candidate");
      return;
    }

    setApiStatus({ loading: true, error: "" });
    try {
      const response = await get(`${uri}/fileservice/api/files/view/${resumeId}`);
      if (response && response.fileContent) {
        const byteCharacters = atob(response.fileContent);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const fileTypeList = response?.fileName?.split(".");
        const fileType = fileTypeList[fileTypeList.length - 1]?.toLowerCase();
        const mimeTypes = {
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        };
        const mimeType = mimeTypes[fileType] || "application/octet-stream";
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);

        if (fileType === "pdf") {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          setFileModal({ open: true, url });
        }
      } else {
        showErrorToast("Resume file content not found");
      }
    } catch (error) {
      console.error(`Error fetching resume ${resumeId}:`, error);
      showErrorToast(`Failed to fetch resume: ${error.message}`);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  // Handle file modal close
  const handleFileModal = () => {
    setFileModal((prev) => ({ ...prev, open: false, url: "" }));
  };

  // Fetch candidates and counts on component mount and when dependencies change
  useEffect(() => {
    fetchCount();
    fetchCandidatesDetails(currentPage, searchQuery, statusFilter);
  }, [fetchCount, fetchCandidatesDetails, currentPage, searchQuery, statusFilter]);

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchCandidatesDetails(page, searchQuery, statusFilter);
    },
    [fetchCandidatesDetails, searchQuery, statusFilter]
  );

  // Handle search query with debounce
  const handleSearchQuery = (e) => {
    const query = e.target.value ?? "";
    setSearchQuery(query);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      fetchCandidatesDetails(1, query, statusFilter);
    }, 300);
  };

  // Handle status filter change
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page on new filter
    fetchCandidatesDetails(1, searchQuery, e.target.value);
  };

  // Summary data for cards
  const summaryData = [
    {
      iconClass: "fas fa-users text-primary",
      label: "Total Candidates",
      value: totalCount.totalCount || 0,
    },
    {
      iconClass: "fas fa-check-circle text-success",
      label: "Completed",
      value: totalCount.completed || 0,
    },
    {
      iconClass: "fas fa-ban text-danger",
      label: "Declined Offer",
      value: totalCount.declinedOffer || 0,
    },
    {
      iconClass: "fas fa-user-check text-success",
      label: "Hired",
      value: totalCount.hired || 0,
    },
    {
      iconClass: "fas fa-hourglass-half text-warning",
      label: "In Progress",
      value: totalCount.inProgress || 0,
    },
    {
      iconClass: "fas fa-pause-circle text-secondary",
      label: "Put on Hold",
      value: totalCount.putOnHold || 0,
    },
    {
      iconClass: "fas fa-times-circle text-danger",
      label: "Rejected",
      value: totalCount.rejected || 0,
    },
  ];

  return (
    <div className="page-content">
      <PageSubHeader
        heading="Candidates"
        routePath="Home"
        pageName="Candidates"
      />
      <Container fluid className="py-0 px-0">
        <Row className="px-4 py-4">
          <SummaryCards data={summaryData} />
          <Row className="align-items-center">
            <Col className="d-flex p-2 mx-2">
              <InputGroup
                size="sm"
                className="elbert-search-group"
                style={{ width: "348px" }}
              >
                <Input
                  placeholder="Search by candidate name"
                  className="elbert-search"
                  value={searchQuery}
                  onChange={handleSearchQuery}
                />
                <InputGroupText>
                  <span className="fa fa-search" />
                </InputGroupText>
              </InputGroup>
              <Input
                type="select"
                name="statusFilter"
                className="form-select"
                style={{ width: "125px", marginLeft: "16px" }}
                value={statusFilter}
                onChange={handleStatusFilter}
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="HIRED">Hired</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PUT_ON_HOLD">Put on Hold</option>
                <option value="REJECTED">Rejected</option>
                <option value="DECLINED_OFFER">Declined Offer</option>
              </Input>
            </Col>
            <Col className="d-flex gap-3 align-items-center justify-content-end">
              <CustomButton
                text="+ Add candidate"
                onClick={() => navigate("/add-new-candidates")}
              />
            </Col>
          </Row>

          <Row className="mt-4 px-4">
            <Card>
              <CardBody>
                {apiStatus.error && (
                  <div className="text-danger">{apiStatus.error}</div>
                )}
                <div className="table-responsive">
                  <Table
                    className="table mb-0"
                    style={{ border: "0.4px solid #D4D4D4" }}
                  >
                    <thead>
                      <tr>
                        <th>Candidate ID</th>
                        <th>Candidate Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Resume</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiStatus.loading ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      ) : candidateDetails && candidateDetails.length > 0 ? (
                        candidateDetails.map((data) => (
                          <tr key={data.id}>
                            <td>{data.candidateId}</td>
                            <td>
                              {data.generalInfoCandidateDto?.firstName}{" "}
                              {data.generalInfoCandidateDto?.lastName}
                            </td>
                            <td>{data.generalInfoCandidateDto?.email}</td>
                            <td>
                              {data.generalInfoCandidateDto?.mobileNumber}
                            </td>
                            <td>
                              {data.resumeId ? (
                                <i
                                  className="fa-solid fa-file-pdf"
                                  title="View Resume"
                                  style={{
                                    color: "#FF0000",
                                    fontSize: "19px",
                                    marginLeft: "5px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleViewResume(data.resumeId)}
                                />
                              ) : (
                                <i
                                  className="fa-solid fa-file-pdf"
                                  title="No Resume"
                                  style={{
                                    color: "#D3D3D3",
                                    fontSize: "19px",
                                    marginLeft: "5px",
                                  }}
                                />
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <i
                                  className="fa-solid fa-eye action-icon"
                                  title="View"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    navigate(`/view-candidate/${data.id}`)
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No candidate documents available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
        <Modal
          isOpen={fileModal.open}
          toggle={handleFileModal}
          centered
          size="lg"
          backdrop="static"
        >
          <ModalHeader toggle={handleFileModal}>View Resume</ModalHeader>
          <ModalBody>
            {fileModal.url ? (
              <iframe
                src={fileModal.url}
                style={{ width: "100%", height: "500px" }}
                title="Resume"
              />
            ) : (
              <div className="text-center">No file available</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleFileModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Candidates;